/**
 * One file with everything needed to work out afterwards what happened in a session: what the
 * controller said and how it was heard, what the sim did with it, what every pilot said and which
 * brain chose the words, the events, and where each aircraft was. The radio text alone could not
 * answer "why did it go around?" or "why did it not slow down?"; this can.
 *
 * `scripts/session-report.mjs` turns the file into one merged timeline, and with a local server
 * database it adds the model traces for the same session (their `session_id` is `transcript.id`).
 */
import type { Sim } from '@atc/sim';
import type { TranscriptSession } from './transcript';

export const SESSION_LOG_VERSION = 1;
/** Sim seconds between position samples. */
const SAMPLE_S = 5;
/** Kept samples, all aircraft together: a few hours of a busy sector. */
const MAX_SAMPLES = 20000;

/**
 * [sim time, callsign, lat, lon, altitude ft, IAS kt, heading, phase, nav mode, assigned speed,
 * approach] — approach is "" (none), or the runway with C (cleared to land) and E (established).
 */
export type TrackSample = [number, string, number, number, number, number, number, string, string, number | null, string];

export interface SessionLog {
  kind: 'atc-session-log';
  version: number;
  exportedAt: string;
  /** The id the server files this session and its model calls under. */
  sessionId: string;
  airport: string;
  simTimeS: number;
  lesson?: { id: string; step?: string; status?: string };
  settings: Record<string, unknown>;
  stats: ReturnType<Sim['stats']>;
  /** The controller side as the player produced it: heard text, interpretation, outcome. */
  transcript: TranscriptSession['entries'];
  /** Everything on frequency, with the effect of each controller line and the brain of each pilot line. */
  radio: ReturnType<Sim['radioLog']>;
  events: ReturnType<Sim['timeline']>;
  brain: ReturnType<Sim['brainLog']>;
  tracks: TrackSample[];
}

const round = (n: number, places: number) => Math.round(n * 10 ** places) / 10 ** places;

/** Samples every aircraft every few sim seconds; cheap enough to leave on for every session. */
export function createTrackRecorder(sim: Pick<Sim, 'simTimeS' | 'internal'>) {
  const samples: TrackSample[] = [];
  let nextS = 0;
  return {
    sample(): void {
      if (sim.simTimeS < nextS) return;
      nextS = sim.simTimeS + SAMPLE_S;
      for (const a of sim.internal()) {
        const ap = a.approach ? `${a.approach.runway.name}${a.approach.cleared ? 'C' : ''}${a.approach.established ? 'E' : ''}` : '';
        samples.push([round(sim.simTimeS, 1), a.callsign, round(a.lat, 5), round(a.lon, 5), Math.round(a.altitudeFt), Math.round(a.iasKt), Math.round(a.headingDeg), a.phase, a.nav.mode, a.assigned.speedKt ?? null, ap]);
      }
      if (samples.length > MAX_SAMPLES) samples.splice(0, samples.length - MAX_SAMPLES);
    },
    tracks: (): TrackSample[] => samples.map((s) => [...s] as TrackSample),
  };
}

export function buildSessionLog(input: {
  sim: Sim;
  transcript: TranscriptSession;
  tracks: TrackSample[];
  lesson?: SessionLog['lesson'];
}): SessionLog {
  const { sim, transcript } = input;
  return {
    kind: 'atc-session-log',
    version: SESSION_LOG_VERSION,
    exportedAt: new Date().toISOString(),
    sessionId: transcript.id,
    airport: transcript.airport,
    simTimeS: round(sim.simTimeS, 1),
    ...(input.lesson ? { lesson: input.lesson } : {}),
    settings: transcript.settings,
    stats: sim.stats(),
    transcript: transcript.entries,
    radio: [...sim.radioLog()],
    events: [...sim.timeline()],
    brain: [...sim.brainLog()],
    tracks: input.tracks,
  };
}

export function downloadSessionLog(log: SessionLog): void {
  const blob = new Blob([JSON.stringify(log)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `atc-session-${log.airport.toLowerCase()}-${log.lesson?.id ?? 'free'}-${log.exportedAt.replace(/[:.]/g, '-')}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
